import {
	type FilterChipsModalItem,
	type FilterChipsModalTab,
	type SelectedFilterChipsModalItems
} from '@capsicum/common';
import { type PrimitiveAtom, useAtom } from 'jotai';
import { type SetStateAction, useEffect, useRef, useState } from 'react';

export const useFilterChipsModal = (
	tabs: Array<FilterChipsModalTab>,
	defaultTab: string,
	closeModal: () => void,
	atom: PrimitiveAtom<SelectedFilterChipsModalItems>,
	onApplyFilters?: (filters: SelectedFilterChipsModalItems) => void
) => {
	const height = window.innerHeight;
	const [getSelectedChipsFilterAtom, setSelectedChipsFilterAtom] = useAtom(atom);

	const [selectedTab, setSelectedTab] = useState<string>(defaultTab);
	const [searchText, setSearchText] = useState<string>('');
	const [shouldClearText, setShouldClearText] = useState<boolean>(false);

	const [selectedItems, setSelectedItems] = useState<SelectedFilterChipsModalItems>(getSelectedChipsFilterAtom);

	const [tabContent, setTabContent] = useState<Array<FilterChipsModalItem>>([]);
	const searchInputRef = useRef<HTMLInputElement>(null) || {};

	const showTabs = tabs.length > 1;

	const handleSearchInputChange = (e: { target: { value: SetStateAction<string> } }) => {
		setSearchText(e.target.value);
	};

	const handleClearSearchInput = () => {
		setSearchText('');
		setShouldClearText(true);
	};

	const handleTabChange = (tab: string) => {
		if (selectedTab !== tab) {
			handleClearSearchInput();
			setSelectedTab(tab);
		}
	};

	useEffect(() => {
		if (searchText === '') {
			setTabContent(tabs.find(tab => tab.name === selectedTab)?.items || []);
		} else {
			setTabContent(
				tabs
					.find(tab => tab.name === selectedTab)
					?.items.filter(
						item =>
							item.label.trim().toLocaleLowerCase().includes(searchText.trim().toLocaleLowerCase()) ||
							item.secondLabel?.trim().toLocaleLowerCase().includes(searchText.trim().toLocaleLowerCase())
					) || []
			);
		}
	}, [searchText, selectedTab, tabs]);

	const handleOnClear = () => {
		const currentSelected = tabs.reduce((acc: SelectedFilterChipsModalItems, val) => {
			acc[val.name] = [];

			return acc;
		}, {});

		setSelectedItems(currentSelected);
		setSelectedChipsFilterAtom(currentSelected);
	};

	const handleApplyFilters = () => {
		onApplyFilters?.(selectedItems);

		setSelectedChipsFilterAtom(selectedItems);
		closeModal();
	};

	const isItemSelected = (item: FilterChipsModalItem) => {
		const isAlreadySelected = selectedItems[item.tabName]?.findIndex(selected => selected.id === item.id);

		if (isAlreadySelected !== undefined && isAlreadySelected >= 0) return true;
		else return false;
	};

	const addItemSelected = (item: FilterChipsModalItem) => {
		const currentSelected = {
			...selectedItems,
			[item.tabName]: [...(selectedItems[item.tabName] || []), item]
		};

		setSelectedItems(currentSelected);
	};

	const removeItemSelected = (item: FilterChipsModalItem) => {
		const currentSelected = {
			...selectedItems,
			[item.tabName]: (selectedItems[item.tabName] || []).filter(selectedItem => selectedItem.id !== item.id)
		};

		setSelectedItems(currentSelected);
	};

	const handleItemSelect = (item: FilterChipsModalItem) => {
		const isAlreadySelected = isItemSelected(item);

		if (isAlreadySelected) {
			removeItemSelected(item);
		} else {
			addItemSelected(item);
		}
	};

	return {
		handleApplyFilters,
		handleClearSearchInput,
		handleItemSelect,
		handleOnClear,
		handleSearchInputChange,
		handleTabChange,
		height,
		isItemSelected,
		removeItemSelected,
		searchInputRef,
		searchText,
		selectedItems,
		selectedTab,
		setSearchText,
		setShouldClearText,
		shouldClearText,
		showTabs,
		tabContent
	};
};
